/* ⚠️ GENERATED from app/services/callResilience.ts by gen-twins.mjs — edit the TypeScript, not this. */
// services/callResilience.ts — a call that survives the network going away.
//
// 🟥 THE BROWSER ENDED THE CALL THE MOMENT ICE FAILED:
//
//      if (conn.connectionState === 'failed') { endCall('failed'); }
//
//    One line, and it is the whole reason a guest whose internet blinks loses
//    the call. `failed` is not "this call is over" — it is "the path I was using
//    is gone", which is exactly what happens when a phone moves from wifi to
//    cellular, a lift door closes, or a router reboots. The path can be rebuilt;
//    the app has done so for a long time. The browser hung up instead.
//
// 🟢 THE APP'S COORDINATOR, AS A POLICY. The phone's WebRTCService already knows
//    how to do this and its constants are tuned by use, so they are mirrored
//    here rather than re-guessed: the two ends of the same call should not
//    disagree about how long to wait before giving up on each other.
//
// 🟥 AND "TELL THE USER" IS A SEPARATE DECISION FROM "START RECOVERING".
//    Recovery begins immediately; the banner waits. A 400ms blip that heals
//    itself should never flash "Reconnecting…" — a UI that flickers on every
//    hiccup teaches people the call is fragile when it is not.

/* ── the phone's numbers, deliberately (server/WebRTCService.ts) ───────────── */
export const ANNOUNCE_DOWN_MS = 1500; // hold the banner this long — most blips heal
export const RETRY_MIN_MS = 2000; // first ICE-restart gap
export const RETRY_MAX_MS = 5000; // backoff ceiling
export const LOOP_CAP_MS = 60000; // stop RETRYING (this does not end the call)
export const DEADLINE_MS = 90000; // no media for this long — now it is over
export const FLAP_WINDOW_MS = 60000; // how far back a "recent drop" counts
export const FLAP_COUNT = 3; // this many recent drops means the link is flaky
export const STABLE_HOLD_MS = 6000; // on a flaky link, hold recovery before saying so
export const SOCKET_GRACE_MS = 2000; // let ICE self-heal before restarting on socket return

/* ══ 🌐 THE NETWORK COMING BACK IS NOT THE SAME EVENT AS ICE NOTICING ═════════
   🟥 EVERY NUMBER ABOVE IS MEASURED FROM THE DROP, and that is the right anchor
      for deciding whether a call is dead. It is the WRONG anchor for deciding
      how long a recovery gets, because the two are not the same question: an
      outage that lasts eighty-five seconds and then ends leaves a recovery five
      seconds to complete an ICE restart, a signalling round trip and a
      connectivity check. It cannot, so the call dies five seconds after the
      network came back — which reads to the person holding the phone as "my
      internet is fine and it hung up on me anyway". Past LOOP_CAP_MS it is worse
      still: the retry loop has stood down, so nothing even tries.

   🟢 A RETURN RE-BASES THE WINDOW. Not extends — re-bases, to leave exactly
      enough for one honest attempt.

   🟥 AND A RE-BASE MUST NOT BE A WAY TO KEEP A DEAD CALL OPEN FOREVER. A link
      that flaps every twenty seconds would re-base indefinitely. So the cap
      below is measured from the FIRST drop of the outage and is never re-based:
      it is the one clock nothing can move. */
export const NETWORK_RETURN_GRACE_MS = 25000; // what one full recovery attempt needs
export const ABSOLUTE_CAP_MS = 180000; // from the first drop; never re-based

/* ── the ring, and when a device may stop it by itself ─────────────────── */

/**
 * 🟥 THE RING WAS FANNED OUT AND THE STOP WAS NOT. `incoming-call` reaches
 *    every device of the callee on purpose. The end of the call — timeout,
 *    cancel, decline — was addressed to one socket, so the phone went quiet and
 *    the browser rang on alone until someone closed the tab.
 *
 * 🟥 AND A STOP THAT HAS TO ARRIVE IS NOT A STOP. Even fanned out, it is one
 *    packet on one socket: a browser that is mid-reconnect at that instant is
 *    told nothing, and nothing else will ever tell it. A ring must therefore
 *    also expire from the inside, on facts the ringing device already holds.
 *
 * 🟢 THE DEADLINE IS THE SERVER'S WINDOW PLUS SLACK, NOT A NUMBER OF ITS OWN.
 *    Ringing past RING_WINDOW_MS only ever means the stop was lost, so the grace
 *    is sized for a late stop (push wake, socket re-attach) and nothing more. Too
 *    short and a device cuts a ring the server still considers live; too long and
 *    it is the bug again, quieter.
 */
/* ═══ HOW LONG A 1-ON-1 CALL RINGS — the ONE table every reader uses ═════════
   The phone app, the browser and the SERVER (generated copies — tools/gen-twins.mjs)
   all read these numbers, and every other ring timer is derived from them, so a
   change here moves the whole system together and the order below can never break.

   🟢 60 s, like WhatsApp: a call rings for about a minute before it is "No answer"
      (iOS's own CallKit ringtone also stops at about a minute). The window starts
      when the callee's DEVICE says it is ringing, not when the caller dialled — push
      transit and a sleeping phone are not charged to the person answering.

   THE ORDER THAT MUST HOLD (and does, by construction):
     window  <  callee's own deadline (window + grace)  <  stale-ring cut-off
             <  hard cap  <  caller's last-resort give-up / server purge
   A device that gave up BEFORE the server would end a call the server still
   considers ringing; a caller that gave up before the server would write
   "No answer" while the callee's phone is still ringing. */
export const RING_WINDOW_MS = 60000; // the server's own ring timeout (from the callee's ring-ack)
export const RING_GRACE_MS = 8000; // slack for a stop that arrives late
export const RING_DEADLINE_MS = RING_WINDOW_MS + RING_GRACE_MS;
/** No call rings past this from DIALLING, whatever any device reports (server clamp). */
export const RING_HARD_CAP_MS = RING_WINDOW_MS * 2;
/** The caller's own give-up when the server's "no answer" is lost — it normally lands first. */
export const CALLER_GIVE_UP_MS = RING_WINDOW_MS + 5000;
/** …and never later than this from dialling (the hard cap plus the same slack). */
export const CALLER_GIVE_UP_CAP_MS = RING_HARD_CAP_MS + 5000;
/** A remembered/held ring (a stored incoming call, a tapped answer) older than this is over. */
export const RING_STALE_MS = RING_WINDOW_MS + 15000;
/** Server: a call still 'ringing' this long after dialling lost its timer — purge it. */
export const RING_PURGE_MS = RING_HARD_CAP_MS + 5000;
/** Server: how long APNs may hold an undelivered VoIP ring push (seconds). */
export const RING_PUSH_EXPIRY_S = Math.ceil(RING_WINDOW_MS / 1000);
/** Clock-skew allowance when a phone judges a server timestamp. */
export const RING_SKEW_MS = 30000;

/**
 * Is this ring push about a call that is already over? A push the OS held back
 * (phone off, deep Doze) can arrive long after the call ended — and an Android
 * call notification then rang for a call nobody was making. The server never
 * lets a call ring past RING_HARD_CAP_MS from dialling, so anything older than
 * that (plus room for the two clocks disagreeing) is a ring for nobody.
 * An unknown start is NOT stale — silence about when it began proves nothing.
 */
export function ringPushStale(initiatedAt, now = Date.now()) {
  const t = Number(initiatedAt);
  if (!Number.isFinite(t) || t <= 0) return false;
  return Number(now) - t > RING_HARD_CAP_MS + RING_SKEW_MS;
}

/**
 * May this device stop ringing on its own, having heard nothing?
 *
 * 🟢 AN UNKNOWN START IS NOT AN EXPIRED ONE. Without a real ring start there
 *    is no elapsed time to judge, and treating that as "expired" would kill a
 *    call the instant it arrives. Silence about when it began is not evidence
 *    that it began long ago.
 */
export function ringExpired(startedAt, now = Date.now()) {
  const t = Number(startedAt);
  if (!Number.isFinite(t) || t <= 0) return false;
  return Number(now) - t >= RING_DEADLINE_MS;
}

/** Milliseconds left on the ring, floored at 0 (0 also for an unknown start). */
export function ringRemaining(startedAt, now = Date.now()) {
  const t = Number(startedAt);
  if (!Number.isFinite(t) || t <= 0) return 0;
  return Math.max(0, RING_DEADLINE_MS - (Number(now) - t));
}

/* ═══ A GROUP RING, AND BEING RUNG AGAIN ═══════════════════════════════════
   🟥 A DECLINED GROUP RING WAS REMEMBERED BY (group, when the call started) —
      and a re-invite is the SAME call, so it carries the same start. The phone
      remembered "I said no to this" and swallowed the second ring: the
      notification came (that is native) and the ring screen never did. Only one
      of the three ways in knew to let a re-invite through, and the one a locked
      or backgrounded Android uses (the full-screen notification) was not it.

   🟢 SO EVERY INVITE IS NUMBERED. The server counts invites per person
      (inviteCount, 1 for the first ring) and sends that number as `inviteSeq`.
      "I declined invite 1" says nothing about invite 2, and a stale redelivery
      of invite 1 is still recognised as stale — no flag to trust, no list of
      paths that have to remember to honour it.

   An invite with no number came from a server older than this rule; for those
   the old `reinvite` flag is still the only thing that can tell a second ring
   from a redelivery, so it is still honoured. */

/** The number of this invite to this person (1 = the first ring), 0 if unknown. */
export function groupInviteSeq(p) {
  const n = Number(p && p.inviteSeq);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/** What "I have dealt with this ring" is remembered by. */
export function groupInviteKey(groupId, callStartedAt, inviteSeq) {
  const seq = groupInviteSeq({
    inviteSeq
  });
  return String(groupId || '') + '::' + String(callStartedAt || '') + (seq ? '#' + seq : '');
}

/** An un-numbered re-invite from an older server: the flag is all there is to go on. */
export function groupInviteLegacyReinvite(p) {
  const flagged = !!p && (p.reinvite === true || p.reinvite === 'true');
  return flagged && groupInviteSeq(p) === 0;
}

/** Is this a ring AFTER the first one — a re-invite, numbered or not? */
export function groupInviteIsRepeat(p) {
  return groupInviteSeq(p) > 1 || groupInviteLegacyReinvite(p);
}

/**
 * Does a decline (or a ring ending) for invite `seq` still speak for the ring
 * that is up now (`currentSeq`)? A CallKit call that a re-invite replaced ends
 * a beat AFTER the new ring is up; that end is about the old invite and must not
 * decline the new one. Unknown numbers on either side keep the old behaviour.
 */
export function groupDeclineIsCurrent(seq, currentSeq) {
  const a = groupInviteSeq({
    inviteSeq: seq
  });
  const b = groupInviteSeq({
    inviteSeq: currentSeq
  });
  if (!a || !b) return true;
  return a >= b;
}

/**
 * Has this link dropped often enough recently that a recovery should be held
 * before it is believed?
 *
 * 🟢 WHY HOLD AT ALL: on a genuinely unstable link, announcing "connected" the
 *    instant ICE says so produces a banner that blinks on and off every few
 *    seconds, which is worse to watch than one that simply stays up until things
 *    settle.
 */
export function isFlaky(dropAts, now = Date.now()) {
  const list = Array.isArray(dropAts) ? dropAts : [];
  return list.filter(t => now - Number(t) < FLAP_WINDOW_MS).length >= FLAP_COUNT;
}

/** How long to wait before the next ICE restart, given how long we have been down. */
export function retryDelay(downForMs) {
  const d = Math.max(0, Number(downForMs) || 0);
  return Math.min(RETRY_MIN_MS + d / 4, RETRY_MAX_MS);
}

/**
 * What the call should do right now.
 *
 * 🟢 ONE FUNCTION, SO THE TWO DECISIONS CANNOT DRIFT APART. Whether to keep
 *    trying and whether to say so are answered from the same facts, in one
 *    place, rather than by two timers that each know half the story.
 *
 * @param s.state       the RTCPeerConnection's connectionState
 * @param s.downSince   when it stopped being connected (0 = it is up)
 * @param s.now
 * @param s.everConnected  a call that has NEVER connected is not "reconnecting"
 * @param s.dropAts     timestamps of recent drops, for the flap test
 * @param s.lastTryAt   when ICE was last restarted
 */
/**
 * 🌐 The device is back online mid-call. Where should the down-clock now sit?
 *
 * Returns the `downSince` to adopt, and whether the call should end instead —
 * which is the case only when the absolute cap has passed, because at that point
 * no number of returns is evidence of a call worth keeping.
 *
 * `firstDropAt` is the start of the OUTAGE, not of this attempt: it survives
 * every re-base, which is what makes the cap a real bound.
 */
export function rebaseOnNetworkReturn(s = {}) {
  const now = Number(s.now) || Date.now();
  const downSince = Number(s.downSince) || 0;
  const firstDropAt = Number(s.firstDropAt) || downSince || now;
  if (now - firstDropAt > ABSOLUTE_CAP_MS) return {
    downSince,
    end: true
  };
  if (!downSince) return {
    downSince: 0,
    end: false
  };
  const left = DEADLINE_MS - (now - downSince);
  if (left >= NETWORK_RETURN_GRACE_MS) return {
    downSince,
    end: false
  };
  /* leave exactly NETWORK_RETURN_GRACE_MS — no more, so a call really dead on a
     link that keeps returning still reaches the cap on schedule */
  return {
    downSince: now - (DEADLINE_MS - NETWORK_RETURN_GRACE_MS),
    end: false
  };
}
export function decide(s = {}) {
  const now = Number(s.now) || Date.now();
  const state = String(s.state || '');
  const downSince = Number(s.downSince) || 0;
  const downFor = downSince ? now - downSince : 0;
  /* 🟢 the clock nothing re-bases — see ABSOLUTE_CAP_MS. Absent (an older caller
     that does not track it) it cannot fire, so behaviour is unchanged. */
  const firstDropAt = Number(s.firstDropAt) || 0;
  if (state === 'connected' || state === 'completed') {
    /* 🟥 A RECOVERY ON A FLAKY LINK IS NOT BELIEVED IMMEDIATELY. */
    return {
      action: 'up',
      hold: isFlaky(s.dropAts, now) ? STABLE_HOLD_MS : 0
    };
  }
  if (state === 'closed') return {
    action: 'none'
  };

  /* 🟥 90 SECONDS WITHOUT MEDIA IS THE ONLY THING THAT ENDS A CALL HERE — not
     `failed`, which merely says the current path is gone. */
  if (firstDropAt && now - firstDropAt > ABSOLUTE_CAP_MS) {
    return {
      action: 'end',
      reason: 'no media for ' + Math.round(ABSOLUTE_CAP_MS / 1000) + 's since the first drop'
    };
  }
  if (downSince && downFor > DEADLINE_MS) return {
    action: 'end',
    reason: 'no media for 90s'
  };
  const isDown = state === 'disconnected' || state === 'failed';
  if (!isDown) return {
    action: 'none'
  };

  /* 🟢 A CALL THAT NEVER CONNECTED IS STILL CONNECTING, and calling that
     "reconnecting" would be a lie the very first time somebody uses this. */
  const announce = s.everConnected === true && downFor >= ANNOUNCE_DOWN_MS;

  /* 🟥 STOP RETRYING, DO NOT STOP THE CALL. Past the loop cap the restarts have
     clearly not helped; the call still has until the deadline for the network to
     come back on its own, and it often does. */
  if (downFor > LOOP_CAP_MS) return {
    action: 'wait',
    announce
  };
  const since = Number(s.lastTryAt) || 0;
  const due = !since || now - since >= retryDelay(downFor);
  return {
    action: due ? 'restart' : 'wait',
    announce,
    in: due ? 0 : retryDelay(downFor) - (now - since)
  };
}
